import { Component, OnInit, Input, OnDestroy, ElementRef, ViewChildren, QueryList } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Message, SelectItem } from 'primeng/api';
import { BehaviorSubject, combineLatest, Observable, Subject, Subscription, filter, map, startWith, tap } from 'rxjs';

import { MessageService } from '@shared/services/message-service';
import { ActivityBlockingService } from '@shared/services/activity-blocking.service';
import { BayType } from '@shared/models/bay-type';
import { ShiftStatus } from '@shared/models/worked-shift';
import { CustomValidators } from '@shared/helpers/custom-validators';
import { saveDebounce } from '@shared/helpers/save-debounce';

import { ProductService } from '../../../../services/product.service';
import { ShiftContextService } from '../../../../services/shift-context.service';
import { InvCountProductCount } from '../../../../models/inv-count-product-count';
import { InventoryService } from '../../../../services/inventory.service';
import { ShiftContext } from '../../../../models/shift-context';

type RowForm = FormGroup<{
    id: FormControl<number>;
    displayOrder: FormControl<number>;
    product: FormControl<number>;
    dim1x: FormControl<number>;
    dim1y: FormControl<number>;
    dim1z: FormControl<number>;
    pieces: FormControl<number>;
    dim2x: FormControl<number>;
    dim2y: FormControl<number>;
    dim2z: FormControl<number>;
}> & {
    totalInventory?: Observable<number>;
};

@Component({
    selector: 'app-bay-inventory',
    templateUrl: './bay-inventory.component.html',
    styleUrls: ['./bay-inventory.component.scss']
})
export class BayInventoryComponent implements OnInit, OnDestroy {

    @Input() inventoryType: string;
    @Input() invCountId: number;

    @Input() set subinventoryId(subinventoryId: number) {
        this.siteSubinventoryId.next(subinventoryId);
    }

    nextBay: number = 0;
    bayInventoryForm = new FormGroup({
        product: new FormControl<number>(null, Validators.required),
        dim1x: new FormControl<number>(0, Validators.required),
        dim1y: new FormControl<number>(0, Validators.required),
        dim1z: new FormControl<number>(0, Validators.required),
        pieces: new FormControl<number>(0, Validators.required),
        dim2x: new FormControl<number>(0),
        dim2y: new FormControl<number>(0),
        dim2z: new FormControl<number>(0)
    });
    bayInventoryFormTotal = 0;

    @ViewChildren('dim1xId') dim1xIdList: QueryList<ElementRef>;
    @ViewChildren('dim2xId') dim2xIdList: QueryList<ElementRef>;
    @ViewChildren('piecesId') piecesIdList: QueryList<ElementRef>;
    @ViewChildren('productId') productIdList: QueryList<ElementRef>;
    @ViewChildren('dim1zForm') dim1zForm: QueryList<ElementRef>;
    @ViewChildren('dim2zForm') dim2zForm: QueryList<ElementRef>;
    @ViewChildren('piecesForm') piecesForm: QueryList<ElementRef>;

    products: SelectItem[] = [];
    subscriptions: Subscription[];
    shutdown: Subject<void> = new Subject<void>();
    context: ShiftContext;
    siteSubinventoryId = new BehaviorSubject<number>(null);
    shiftClosed: boolean = false;

    // begin bay inventory item grid variables
    bayInventoryItems: RowForm[] = [];
    autoSaveFormSubscriptions: Subscription[];
    // end bay inventory item grid variables

    readonly _bayInventoryItemNotCreatedMsg: Message = {
        severity: 'warn',
        summary: 'Could not Create Bay Inventory Item',
        detail: 'Fields are required.'
    };

    readonly _bayInventoryItemCreatedMsg: Message = {
        severity: 'success',
        summary: 'Bay Inventory Item Created',
        detail: ''
    };

    readonly _bayInventoryItemDeletedMsg: Message = {
        severity: 'warn',
        summary: 'Bay Inventory Item Deleted',
        detail: ''
    };

    constructor(
        private productService: ProductService,
        private shiftContextService: ShiftContextService,
        private messageService: MessageService,
        private inventoryService: InventoryService,
        private activityService: ActivityBlockingService) {
    }

    ngOnInit() {
        const siteObservable = this.shiftContextService.CurrentContext
            .pipe(tap(context => {
                this.context = context;

                this.shiftClosed = context.status !== ShiftStatus.Open;

                if (this.shiftClosed) {
                    this.bayInventoryForm.disable();
                    this.bayInventoryItems.forEach(i => i.disable({ emitEvent: false }));
                }
                else {
                    this.bayInventoryForm.enable();
                    this.bayInventoryItems.forEach(i => i.enable({ emitEvent: false }));
                }
            }));

        const productsObservable = this.productService.ProductsForSubinventory(this.siteSubinventoryId)
            .pipe(tap(products => {
                this.products = products.map(p => ({ value: p.id, label: p.label }));
            }));

        const bayInventoryObservable = this.inventoryService.CurrentInvProductCount.pipe(
            filter(counts => !!counts),
            tap(counts => {
                const bayInventoryItems: RowForm[] = counts
                    .filter(x => x.bayType === BayType.PalletBay)
                    .sort((a, b) => a.displayOrder - b.displayOrder)
                    .map(bay => this.addFormGroup(bay));

                bayInventoryItems.forEach(bay => {
                    bay.totalInventory = bay.valueChanges.pipe(
                        startWith(this.calculateTotal(bay.controls)),
                        map(() => this.calculateTotal(bay.controls)));

                        if (this.shiftClosed)
                            bay.disable({ emitEvent: false });
                    });

                this.autoSaveFormSubscriptions?.forEach(s => s.unsubscribe());
                this.autoSaveFormSubscriptions = bayInventoryItems
                    .map(bay => bay.valueChanges
                        .pipe(saveDebounce(this.shutdown))
                        .subscribe(b => this.onChangeBayInventoryField(bay)));

                this.bayInventoryItems = bayInventoryItems;

                this.nextBay = this.reIndexBay(bayInventoryItems);
            }));

        this.subscriptions = [
            combineLatest([siteObservable, productsObservable, bayInventoryObservable])
                .pipe(this.activityService.Wait('Bay Inventory'))
                .subscribe(),

            this.bayInventoryForm.valueChanges.subscribe(() => this.calculateFormTotal())
        ];
    }

    ngOnDestroy() {
        this.shutdown.next();
        this.subscriptions.forEach(s => s.unsubscribe());
        this.autoSaveFormSubscriptions.forEach(s => s.unsubscribe());
        this.bayInventoryItems = [];
    }

    onChangeBayInventoryField(bayInventoryItem: RowForm) {
        const vals: InvCountProductCount = {
            id: bayInventoryItem.controls.id.value,
            productId: bayInventoryItem.controls.product.value,
            bayType: BayType.PalletBay,
            invCountId: this.invCountId,
            dim1x: +bayInventoryItem.controls.dim1x.value,
            dim1y: +bayInventoryItem.controls.dim1y.value,
            dim1z: +bayInventoryItem.controls.dim1z.value,
            dim2x: +bayInventoryItem.controls.dim2x.value,
            dim2y: +bayInventoryItem.controls.dim2y.value,
            dim2z: +bayInventoryItem.controls.dim2z.value,
            pieces1: +bayInventoryItem.controls.pieces.value
        };

        this.inventoryService.updateInvCountProductCount(vals).subscribe({
            next: () => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Bay Inventory Updated',
                    detail: ''
                });
            },
            error: _ => {}
        });
    }

    saveBI() {
        if (!this.bayInventoryForm.valid) {
            this.bayInventoryForm.markAllAsTouched();
            this.messageService.add(this._bayInventoryItemNotCreatedMsg);
        } else {
            const newItem: InvCountProductCount = {
                id: 0,
                invCountId: this.invCountId,
                bayType: BayType.PalletBay,
                displayOrder: this.nextBay,
                productId: this.bayInventoryForm.controls.product.value,
                dim1x: this.bayInventoryForm.controls.dim1x.value,
                dim1y: this.bayInventoryForm.controls.dim1y.value,
                dim1z: this.bayInventoryForm.controls.dim1z.value,
                dim2x: this.bayInventoryForm.controls.dim2x.value,
                dim2y: this.bayInventoryForm.controls.dim2y.value,
                dim2z: this.bayInventoryForm.controls.dim2z.value,
                pieces1: this.bayInventoryForm.controls.pieces.value
            };

            this.inventoryService.createInvCountProductCount(newItem).subscribe(created => {
                const form = this.addFormGroup(created);
                this.bayInventoryItems = [ ...this.bayInventoryItems, form ];

                this.autoSaveFormSubscriptions.push(form.valueChanges
                    .pipe(saveDebounce(this.shutdown))
                    .subscribe(b => this.onChangeBayInventoryField(form)));

                this.messageService.add(this._bayInventoryItemCreatedMsg);
                this.bayInventoryForm.reset({
                    product: null,
                    dim1x: 0, dim1y: 0, dim1z: 0,
                    dim2x: 0, dim2y: 0, dim2z: 0,
                    pieces: 0
                });

                this.nextBay = this.reIndexBay(this.bayInventoryItems);
            });
        }
    }

    addFormGroup(bay: InvCountProductCount) {
        return new FormGroup({
            id: new FormControl<number>(bay.id),
            displayOrder: new FormControl<number>(bay.displayOrder),
            product: new FormControl<number>(bay.productId, Validators.required),
            dim1x: new FormControl<number>(bay.dim1x, [Validators.required, CustomValidators.numeric(false)]),
            dim1y: new FormControl<number>(bay.dim1y, [Validators.required, CustomValidators.numeric(false)]),
            dim1z: new FormControl<number>(bay.dim1z, [Validators.required, CustomValidators.numeric(false)]),
            pieces: new FormControl<number>(bay.pieces1, [Validators.required, CustomValidators.numeric(false)]),
            dim2x: new FormControl<number>(bay.dim2x ?? 0, [CustomValidators.numeric(false)]),
            dim2y: new FormControl<number>(bay.dim2y ?? 0, [CustomValidators.numeric(false)]),
            dim2z: new FormControl<number>(bay.dim2z ?? 0, [CustomValidators.numeric(false)])
        });
    }

    deleteBay(bayInventoryItem: RowForm) {
        this.inventoryService.deleteInvCountProductCount(bayInventoryItem.controls.id.value).subscribe(() => {
            this.bayInventoryItems.splice(this.bayInventoryItems.indexOf(bayInventoryItem), 1);
            this.messageService.add(this._bayInventoryItemDeletedMsg);
        });
    }

    calculateFormTotal() {
        this.bayInventoryFormTotal = this.calculateTotal(this.bayInventoryForm.controls);
    }

    calculateTotal(controls: {
            dim1x: FormControl<number>,
            dim1y: FormControl<number>,
            dim1z: FormControl<number>,
            dim2x: FormControl<number>,
            dim2y: FormControl<number>,
            dim2z: FormControl<number>,
            pieces: FormControl<number>
    }): number {
        const dimensionOne = controls.dim1x.value * controls.dim1y.value * controls.dim1z.value;
        const dimensionTwo = controls.dim2x.value * controls.dim2y.value * controls.dim2z.value;
        return +dimensionOne + dimensionTwo + controls.pieces.value;
    }

    get bayInventoryTotal(): number {
        return (this.bayInventoryItems ?? []).reduce(
            (sum, item) => sum + this.calculateTotal(item.controls),
            0
        );
    }

    reIndexBay(items: RowForm[]): number {
        let bayIndex = 1;

        if (items) {
            for (const bayInventoryItem of items.sort((l, r) =>
                    l.controls.displayOrder.value - r.controls.displayOrder.value)) {
                bayInventoryItem.controls.displayOrder.setValue(bayIndex, { emitEvent: false, onlySelf: true });
                bayIndex++;
            }
        }

        return bayIndex++;
    }

    addbuttonFormfocus() {
        const element = this.piecesForm.toArray()[0];
        element.nativeElement.focus();
    }

    rowTrackBy(_i: number, item: RowForm): number {
        return item.controls.id.value;
    }
}

